import React, { useState, forwardRef, type CSSProperties, type ReactNode } from 'react';
import { useAppTheme } from '../contexts/ThemeContext';
import type { AppTheme } from '../theme/theme';

type ButtonVariant = 'filled' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';

type SpinnerProps = {
  size?: number;
  color?: string;
};

const Spinner = ({ size = 14, color = 'currentColor' }: SpinnerProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    style={{ animation: 'button-spin 0.8s linear infinite' }}
  >
    <style>{`@keyframes button-spin { to { transform: rotate(360deg); } }`}</style>
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="3" opacity="0.25" />
    <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth="3" strokeLinecap="round" />
  </svg>
);

type ButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> & {
  variant?: ButtonVariant;
  color?: string;
  size?: ButtonSize;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  iconOnly?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  style?: CSSProperties;
  children?: ReactNode;
};

type ComputedStyle = CSSProperties & {
  minWidth?: string;
};

const getSizeMap = (theme: AppTheme) => ({
  sm: { padding: `${theme.spacing.xs} ${theme.spacing.sm}`, fontSize: theme.fontSize.sm },
  md: { padding: `${theme.spacing.sm} ${theme.spacing.md}`, fontSize: theme.fontSize.md },
  lg: { padding: `${theme.spacing.md} ${theme.spacing.lg}`, fontSize: theme.fontSize.lg },
});

const iconSizeMap: Record<ButtonSize, string> = { sm: '28px', md: '36px', lg: '44px' };

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'filled',
      color = 'core',
      size = 'md',
      startIcon,
      endIcon,
      iconOnly = false,
      loading = false,
      fullWidth = false,
      disabled = false,
      style,
      children,
      ...rest
    },
    ref,
  ) => {
    const { theme } = useAppTheme();
    const [hovered, setHovered] = useState(false);

    const colorMap = theme.colors.button as Record<string, { bg: string; hoverBg?: string; text: string }>;
    const sizeMap = getSizeMap(theme);
    const palette = colorMap[color] || colorMap.core;
    const sizeValues = sizeMap[size] || sizeMap.md;

    const getStyles = (): ComputedStyle => {
      const base: ComputedStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing.xs,
        border: 'none',
        borderRadius: theme.borderRadius.sm,
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        fontSize: sizeValues.fontSize,
        fontWeight: theme.fontWeight.medium,
        fontFamily: theme.fontFamily.primary,
        transition: theme.transitions.fast,
        opacity: disabled ? 0.5 : 1,
        width: fullWidth ? '100%' : undefined,
        lineHeight: '1',
        boxSizing: 'border-box',
      };

      if (iconOnly) {
        const dim = iconSizeMap[size] || iconSizeMap.md;
        base.padding = '0';
        base.width = fullWidth ? '100%' : dim;
        base.height = dim;
        base.minWidth = dim;
      } else {
        base.padding = sizeValues.padding;
      }

      if (variant === 'filled') {
        base.backgroundColor = hovered && !disabled ? palette.hoverBg || palette.bg : palette.bg;
        base.color = palette.text;
      } else if (variant === 'outline') {
        base.border = `2px solid ${palette.bg}`;
        if (hovered && !disabled) {
          base.backgroundColor = palette.bg;
          base.color = palette.text;
        } else {
          base.backgroundColor = 'transparent';
          base.color = palette.bg;
        }
      } else if (variant === 'ghost') {
        base.backgroundColor =
          hovered && !disabled
            ? theme.colors.background_hover?.button || theme.colors.background?.sidebar
            : 'transparent';
        base.color = theme.colors.text?.primary || palette.bg;
      }

      return base;
    };

    const computedStyle = { ...getStyles(), ...style };
    const renderIcon = loading ? <Spinner size={14} /> : startIcon;

    return (
      <button
        ref={ref}
        style={computedStyle}
        disabled={disabled || loading}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        {...rest}
      >
        {renderIcon && (
          <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>{renderIcon}</span>
        )}
        {!iconOnly && children}
        {endIcon && !loading && (
          <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>{endIcon}</span>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
